import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Share2, Printer, Link as LinkIcon, Activity } from 'lucide-react';

import { eventReportApi } from './eventReportApi';
import { EventReportView } from './EventReportView';
import { DemographicsPanel } from '@/features/demographics/DemographicsPanel';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 02 — admin-facing event report. Renders `EventReportView` plus the
 * share-link + print controls. Uses the browser's native print dialog for
 * PDF export until the Puppeteer pipeline lands.
 */
export function EventReportPage() {
  const { id } = useParams<{ id: string }>();
  const eventId = Number(id);
  const navigate = useNavigate();
  const t = useT();
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['eventReport', 'internal', eventId],
    queryFn: () => eventReportApi.internal(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
  });

  const createShare = useMutation({
    mutationFn: () => eventReportApi.createShare(eventId),
    onSuccess: (res) => {
      const fullUrl = `${window.location.origin}${res.url}`;
      setShareUrl(fullUrl);
      navigator.clipboard?.writeText(fullUrl).catch(() => undefined);
    },
  });

  if (!Number.isFinite(eventId) || eventId <= 0) {
    return <div className="page">{t('eventReport.invalidId')}</div>;
  }
  if (isLoading || !data) {
    return (
      <div className="page space-y-4">
        <Skeleton className="h-10" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="page space-y-6 print:p-0">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <button
          type="button"
          onClick={() => navigate(`/events/${eventId}`)}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('eventReport.backToEvent')}
        </button>
        <div className="flex gap-2">
          {/* Caso 10 — entry to the polling live monitor. */}
          <Button
            variant="secondary"
            onClick={() => navigate(`/events/${eventId}/live`)}
          >
            <Activity className="mr-2 h-4 w-4" />
            {t('eventReport.openLiveMonitor')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => window.print()}
          >
            <Printer className="mr-2 h-4 w-4" />
            {t('eventReport.print')}
          </Button>
          <Button
            variant="primary"
            onClick={() => createShare.mutate()}
            disabled={createShare.isPending}
          >
            <Share2 className="mr-2 h-4 w-4" />
            {t('eventReport.createShare')}
          </Button>
        </div>
      </div>

      {shareUrl && (
        <div className="flex items-center gap-2 rounded-md border border-[var(--color-accent)] bg-[var(--color-accent-soft,rgba(178,98,245,0.08))] p-3 text-sm print:hidden">
          <LinkIcon className="h-4 w-4 text-[var(--color-accent)]" />
          <code className="flex-1 break-all">{shareUrl}</code>
          <span className="text-xs text-[var(--color-fg-muted)]">
            {t('eventReport.shareCopied')}
          </span>
        </div>
      )}

      <EventReportView report={data} variant="internal" />

      {/* Caso 09 — Demographic snapshot for the event. Auto-hides when no
          demographic interactions exist yet. */}
      <DemographicsPanel eventId={eventId} variant="event" />
    </div>
  );
}
